import { useState } from "react";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import { DeletionRequest, useDeletionRequests, useDeletionHelpers } from "../hooks/useDeletionRequests";
import { EyeIcon, XMarkIcon } from "@heroicons/react/24/outline";

const statusStyles: Record<string, string> = {
    pending: "bg-orange-100 text-orange-800",
    confirmed: "bg-blue-100 text-blue-800",
    ready: "bg-purple-100 text-purple-800",
    fulfilled: "bg-green-100 text-green-800",
    cancelled: "bg-gray-100 text-gray-600",
};

const DeletionRequestsPage: React.FC = () => {
    const { requests, cancelRequest, pendingCount, readyCount, loading } = useDeletionRequests();
    const { formatDate } = useDeletionHelpers();

    const [selected, setSelected] = useState<DeletionRequest | null>(null);
    const [cancelling, setCancelling] = useState(false);

    const handleCancel = async (uid: string) => {
        setCancelling(true);
        try {
            await cancelRequest(uid);
            setSelected(null);
        } catch (error) {
            console.error("Error cancelling deletion request:", error);
        }
        setCancelling(false);
    };

    return (
        <DashboardWrapper>
            <div className="w-full flex flex-col gap-4 md:gap-6 p-4 md:p-6 md:h-[calc(100vh-64px)] overflow-hidden">
                <div>
                    <h1 className="text-xl md:text-2xl font-heading text-gray-400">Account Deletion Requests</h1>
                    <p className="text-xs text-gray-400 mt-1">
                        Read-only trace log — accounts are deleted by users themselves, never from here. Records are kept permanently for legal/invoice purposes.
                    </p>
                </div>

                <div className="w-full grid grid-cols-3 gap-2 shrink-0">
                    <div className="w-full bg-white rounded-xl flex flex-col p-3 md:p-4 gap-1 md:gap-2 border">
                        <div className="text-xl md:text-2xl text-gray-400 font-bold">{requests.length}</div>
                        <div className="text-[10px] md:text-xs text-gray-500 uppercase">Total Requests</div>
                    </div>
                    <div className="w-full bg-white rounded-xl flex flex-col p-3 md:p-4 gap-1 md:gap-2 border">
                        <div className="text-xl md:text-2xl text-gray-400 font-bold">{pendingCount}</div>
                        <div className="text-[10px] md:text-xs text-gray-500 uppercase">Pending / Confirmed</div>
                    </div>
                    <div className="w-full bg-white rounded-xl flex flex-col p-3 md:p-4 gap-1 md:gap-2 border">
                        <div className="text-xl md:text-2xl text-gray-400 font-bold">{readyCount}</div>
                        <div className="text-[10px] md:text-xs text-gray-500 uppercase">Ready to Complete</div>
                    </div>
                </div>

                <div className="flex-1 min-h-0 bg-white rounded-xl border overflow-hidden flex flex-col">
                    <div className="grid grid-cols-[1fr_auto_auto] md:grid-cols-[2fr_1.5fr_3fr_1fr_auto] gap-2 md:gap-4 px-4 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase shrink-0">
                        <div>Email</div>
                        <div className="hidden md:block">Requested</div>
                        <div className="hidden md:block">Reason</div>
                        <div>Status</div>
                        <div className="hidden md:block">Actions</div>
                    </div>

                    <div className="flex-1 overflow-y-auto min-h-0">
                        {loading ? (
                            <div className="flex items-center justify-center h-32 text-gray-400">Loading requests...</div>
                        ) : requests.length === 0 ? (
                            <div className="flex items-center justify-center h-32 text-gray-400">No deletion requests found</div>
                        ) : (
                            requests.map((item) => (
                                <div
                                    key={item.uid}
                                    className="grid grid-cols-[1fr_auto_auto] md:grid-cols-[2fr_1.5fr_3fr_1fr_auto] gap-2 md:gap-4 px-4 py-3 border-b hover:bg-gray-50 items-center transition-colors cursor-pointer md:cursor-default"
                                    onClick={() => {
                                        if (window.innerWidth < 768) setSelected(item);
                                    }}
                                >
                                    <div className="flex flex-col gap-0.5 min-w-0">
                                        <span className="text-sm text-gray-600 truncate">{item.email}</span>
                                        <span className="text-xs text-gray-400 md:hidden">{formatDate(item.requestedAt)}</span>
                                    </div>

                                    <div className="hidden md:block">
                                        <span className="text-sm text-gray-600">{formatDate(item.requestedAt)}</span>
                                    </div>

                                    <div className="hidden md:block min-w-0">
                                        <p className="text-sm text-gray-600 line-clamp-2">{item.reason || "—"}</p>
                                    </div>

                                    <div className="flex items-center">
                                        <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase rounded-full whitespace-nowrap ${statusStyles[item.status]}`}>
                                            {item.status}
                                        </span>
                                    </div>

                                    <div className="flex items-center justify-end gap-1 md:gap-2">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setSelected(item); }}
                                            className="p-1.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-700"
                                            title="View details"
                                        >
                                            <EyeIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {selected && (
                    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black bg-opacity-50">
                        <div className="bg-white rounded-t-lg sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-center px-4 sm:px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
                                <h2 className="text-lg font-heading">Deletion Request</h2>
                                <button onClick={() => setSelected(null)} className="p-2 hover:bg-gray-100 rounded-full">
                                    <XMarkIcon className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="px-4 sm:px-6 py-4 space-y-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Email</label>
                                    <p className="text-sm">{selected.email}</p>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">User ID</label>
                                    <code className="text-xs sm:text-sm bg-gray-100 px-2 py-1 rounded block overflow-x-auto">{selected.uid}</code>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Username</label>
                                    <p className="text-sm">{selected.username || "—"}</p>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Stripe ID</label>
                                        <p className="text-sm truncate">{selected.stripeId || "—"}</p>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Intercom ID</label>
                                        <p className="text-sm truncate">{selected.intercomId || "—"}</p>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Reason</label>
                                    <p className="text-sm whitespace-pre-line bg-gray-50 p-3 rounded-lg">{selected.reason || "—"}</p>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Status</label>
                                    <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase rounded-full ${statusStyles[selected.status]}`}>
                                        {selected.status}
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Requested</label>
                                        <p className="text-sm">{formatDate(selected.requestedAt)}</p>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Confirmed via email</label>
                                        <p className="text-sm">{selected.confirmedViaEmail ? formatDate(selected.confirmedAt) : "Not yet"}</p>
                                    </div>
                                </div>
                                {selected.reminderSentAt && (
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Ready-to-complete email sent</label>
                                        <p className="text-sm">{formatDate(selected.reminderSentAt)}</p>
                                    </div>
                                )}
                                {selected.fulfilledAt && (
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Fulfilled by user</label>
                                        <p className="text-sm">{formatDate(selected.fulfilledAt)}</p>
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-between gap-2 px-4 sm:px-6 py-4 border-t">
                                {selected.status !== "fulfilled" && selected.status !== "cancelled" ? (
                                    <button
                                        onClick={() => handleCancel(selected.uid)}
                                        disabled={cancelling}
                                        className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                                    >
                                        {cancelling ? "Cancelling..." : "Cancel Request"}
                                    </button>
                                ) : <span />}
                                <button
                                    onClick={() => setSelected(null)}
                                    className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardWrapper>
    );
};

export default DeletionRequestsPage;
